/* eslint-disable @typescript-eslint/no-explicit-any */
import { createRef, type Ref, useRef } from 'react';
import { Container } from '@adobe/aem-react-editable-components';
import flatMap from 'lodash/flatMap';
import { MegaMenu } from './MegaMenu';
import { SubNavigation } from './SubNavigation';
import { NavigationUtilityLink } from './NavigationUtilityLink';
import { BrandRibbon } from '../../organisms/BrandRibbon';
import Link from 'next/link';
import clsx from 'clsx';
import { Button } from '@marriott/mi-ui-library';
import { PrimaryNavEnum, ComponentTypeEnum } from './Navigation.types';
import type { PrimaryNavItemProps, PrimaryNavItemWrapperProps, AemContainerConfig } from './Navigation.types';
import { constants, sortAEMModelItemsByOrder } from '@marriott/mi-ui-library';
import type { AEMModel } from '@marriott/mi-ui-library';
import { getClickTrackValue } from '../../utils/TrackingPropertyUtils';
import { DEFAULT_TRACKING_VALUES } from '../../utils/constants';
import { BrandRibbonVariation } from '../../organisms/BrandRibbon/BrandRibbon.types';

export const PrimaryNavItem = (props: PrimaryNavItemWrapperProps) => {
  const {
    cqPath = '',
    isAuthorMode,
    primaryNavigationItem = {},
    index,
    changeNavOverFlowStyle,
    activeMainNav,
    setActiveMainNav,
    primaryNavRefs,
    isFocused,
    setIsFocused,
    utilityNavigation,
    setIsMegaMenuActive,
    megaMenuCloseButtonLabel,
    featureFlagLogo,
  } = props;

  const { MEGA_MENU, BRAND_RIBBON } = ComponentTypeEnum;
  const { PANEL_TITLE, PANEL_URL } = PrimaryNavEnum;
  const { TARGET_BLANK } = constants;

  const resourceType = primaryNavigationItem?.[':type'] || '';
  const resourceItems = primaryNavigationItem?.[':items'] || {};
  const dynamicData = primaryNavigationItem?.['backgroundMedia'] || {};
  const megaMenuItems: AEMModel = resourceItems['megaMenuItems'] || {};
  const cardArticleData = primaryNavigationItem?.['resourceProperties'] || {};
  const trackingProperties = primaryNavigationItem?.trackingProperties;

  const primaryNavItem: PrimaryNavItemProps =
    resourceType === MEGA_MENU ? primaryNavigationItem?.resourceProperties : primaryNavigationItem;

  const navItemTitle = primaryNavItem?.[PANEL_TITLE] || primaryNavItem?.cqPanelTitle || '';
  const navItemUrl = primaryNavItem?.[PANEL_URL] || '';

  const isSubNavAvailable = !!(!navItemUrl && megaMenuItems && megaMenuItems[':itemsOrder']?.length);
  const isBrandRibbonAvailable = resourceType === BRAND_RIBBON;
  const isContentAvailable = isSubNavAvailable || isBrandRibbonAvailable;

  const isArticleCardEnabled =
    cardArticleData &&
    cardArticleData.ctalink &&
    cardArticleData.ctalabel &&
    cardArticleData.descriptiontext &&
    cardArticleData.titletext &&
    dynamicData;
  const PrimaryMenuItemConfig: AemContainerConfig = {
    emptyLabel: 'Marriott Primary Navitem Container',
    isEmpty: () => false,
    resourceType,
    cqPath: cqPath,
    isInEditor: !!isAuthorMode,
  };
  const subNavItems: any =
    (resourceType === MEGA_MENU && sortAEMModelItemsByOrder(megaMenuItems)) ||
    (resourceType === BRAND_RIBBON && flatMap(primaryNavItem?.categorylists?.map(list => list.linklists))) ||
    [];

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const subNavItemRefs: React.MutableRefObject<HTMLElement | HTMLAnchorElement | null>[] =
    subNavItems?.map((_: any) => createRef()) || [];
  const articleCardRef = useRef<HTMLButtonElement | null>(null);
  const exploreAllRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const subNavRefs = [
    ...subNavItemRefs,
    ...(isArticleCardEnabled ? [articleCardRef] : []),
    ...(isBrandRibbonAvailable ? [exploreAllRef] : []),
    closeButtonRef,
  ];

  const handleMegaMenuState = (message: string, id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.textContent = message;
  };

  const handleMegaMenuKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const { key } = event;
    const itemsLength = subNavRefs.length;
    const currentIndex = subNavRefs.findIndex(linkRef => linkRef.current === document.activeElement);

    if (key === 'Escape') {
      return handleMegaMenuClose();
    }
    if (key === 'ArrowUp' || key === 'ArrowLeft' || (event.shiftKey && key === 'Tab')) {
      if (currentIndex === 0) {
        primaryNavRefs.current[index].focus();
      }
      event.preventDefault();
      subNavRefs[currentIndex - 1]?.current?.focus();
    } else if (key === 'ArrowDown' || key === 'ArrowRight' || key === 'Tab') {
      event.preventDefault();
      if (currentIndex < itemsLength - 1) {
        subNavRefs[currentIndex + 1]?.current?.focus();
      } else if (currentIndex === itemsLength - 1) {
        primaryNavRefs.current[index + 1].focus();
      }
    }
  };

  const handleMainNavKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLAnchorElement>,
    url: string,
    title: string
  ) => {
    const { key } = event;

    if (key === 'ArrowDown' && subNavItems.length > 0) {
      if (activeMainNav !== index) {
        handleMainNavClick(index, event, url, title);
      }
      setTimeout(() => subNavRefs[0]?.current?.focus());
    }

    if (key === 'ArrowLeft' || (event.shiftKey && key === 'Tab')) {
      if (activeMainNav === index - 1) {
        event.preventDefault();
        document.getElementById('close-megamenu-button')?.focus();
      } else if (index > 0) {
        event.preventDefault();
        primaryNavRefs.current[index - 1].focus();
      }
    } else if (key === 'ArrowRight' || key === 'Tab') {
      if (activeMainNav === index) {
        event.preventDefault();
        subNavRefs[0]?.current?.focus();
      } else if (index < primaryNavRefs.current.length - 1) {
        event.preventDefault();
        primaryNavRefs.current[index + 1].focus();
      }
    }
    if (key === 'Enter' || key === ' ') {
      handleMainNavClick(index, event, url, title);
    }
  };

  //shows mega-menu when primary nav link is clicked
  const handleMainNavClick = (index: number, event: any, url?: string, title?: string) => {
    if (url) return;

    event.preventDefault();
    if (activeMainNav === index) {
      handleMegaMenuClose();
      return;
    }
    setIsMegaMenuActive(true);
    handleMegaMenuState(`${title} MegaMenu expanded`, 'nav-item-sr-status');
    setActiveMainNav && setActiveMainNav(index);
  };

  //closes mega menu when close or back button is clicked
  const handleMegaMenuClose = () => {
    if (!navItemUrl) {
      handleMegaMenuState(`${navItemTitle} MegaMenu collapsed`, 'nav-item-sr-status');
    }
    setIsMegaMenuActive(false);
    setActiveMainNav && setActiveMainNav(null);
    primaryNavRefs.current[index].focus();
  };

  const trackingValue = getClickTrackValue({
    trackingProperties,
    url: null,
    fallbacks: {
      position: DEFAULT_TRACKING_VALUES.GLOBAL_NAV,
      description: navItemTitle || DEFAULT_TRACKING_VALUES.LABEL_NULLISH,
    },
  });

  const primaryNavItemNode = (
    <li className="main-nav__nav-item" key={index} role="presentation">
      <Link
        href={navItemUrl}
        className={clsx('m-nav-link custom_click_track', { focused: isFocused }, { active: activeMainNav === index })}
        role="menuitem"
        aria-current={isFocused ? 'page' : undefined}
        aria-label={navItemTitle}
        onFocus={() => setIsFocused && setIsFocused(true)}
        onBlur={() => setIsFocused && setIsFocused(false)}
        tabIndex={0}
        ref={element => element && (primaryNavRefs.current[index] = element)}
        target={primaryNavItem?.['openInaNewTab'] ? TARGET_BLANK : '_self'}
        onKeyDown={event => handleMainNavKeyDown(index, event, navItemUrl, navItemTitle)}
        onClick={event => handleMainNavClick(index, event, navItemUrl, navItemTitle)}
        data-custom_click_track_value={trackingValue}
      >
        {navItemTitle}
        {isContentAvailable && (
          <span className="icon-arrow-right main-nav__nav-link-arrow d-lg-none" data-testid="menu-right-arrow"></span>
        )}
      </Link>

      {activeMainNav === index && isContentAvailable && (
        <MegaMenu
          handleCloseButtonClick={handleMegaMenuClose}
          changeNavOverFlowStyle={changeNavOverFlowStyle}
          currentMainNav={navItemTitle}
          megaMenuCloseButtonLabel={megaMenuCloseButtonLabel}
          handleKeyDown={handleMegaMenuKeyDown}
          ref={closeButtonRef}
        >
          {isSubNavAvailable && (
            <SubNavigation
              megamenuitems={megaMenuItems}
              maxItemsPerColumn={primaryNavItem?.maxColumnItems}
              subNavItems={subNavItems}
              // TODO: i18nOpensInNewTab value should be replaced with the value coming from AEM during integration story
              i18nOpensInNewTab="Opens in a New Tab"
              // TODO: isArticleCardEnabled value should be replaced with the value coming from AEM during Article card story: https://jira.marriott.com/browse/FSD-77628
              cardArticleData={cardArticleData}
              dynamicData={dynamicData}
              handleMegaMenuClose={handleMegaMenuClose}
              isArticleCardEnabled={isArticleCardEnabled}
              forwardedRefs={subNavItemRefs}
              articleCardRef={articleCardRef}
              primaryParentLabel={navItemTitle}
            />
          )}
          {isBrandRibbonAvailable && (
            <>
              <BrandRibbon
                variations={BrandRibbonVariation.PortfolioBrands}
                categorylists={primaryNavItem?.categorylists || []}
                forwardedRefs={subNavItemRefs as Ref<HTMLAnchorElement | null>[]}
                featureFlagLogo={featureFlagLogo}
              />
              <div>
                <Button
                  className={clsx('exlpore-all m-link-tertiary-button mb-3', {
                    'm-link-tertiary-button-external': primaryNavItem?.['openInaNewTab'] === 'true',
                  })}
                  isLink={true}
                  target={primaryNavItem?.['openInaNewTab'] === 'true' ? TARGET_BLANK : '_self'}
                  href={primaryNavItem?.cqPanelUrl}
                  ref={exploreAllRef}
                  buttonCopy={primaryNavItem?.exploreLabel}
                />
              </div>
            </>
          )}
          {utilityNavigation?.[':items'] && (
            <div className="sub-nav-utility-container d-lg-none">
              <NavigationUtilityLink
                utilityNavigation={utilityNavigation}
                isRenderedInsideOverlay={true}
                isBrandedVariation={false}
              />
            </div>
          )}
        </MegaMenu>
      )}
      <div id="nav-item-sr-status" aria-live="polite" className="sr-only"></div>
    </li>
  );

  return (
    <Container {...PrimaryMenuItemConfig} className={clsx({ 'py-0': isAuthorMode })} childPages={primaryNavItemNode} />
  );
};
